import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  VStack,
  HStack,
  Button,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Badge,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useBreakpointValue,
  Link,
  Image,
  Select,
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import { FiEdit, FiTrash2, FiPlus, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import { ieemIframeApi } from '../config/api';
import StatusChip from './bundling/StatusChip';
import ReadinessPanel from './bundling/ReadinessPanel';
import DataTableShell from './bundling/DataTableShell';
import SubheaderToolbar from './bundling/SubheaderToolbar';

const getIframeStatus = (row) => {
  const hasUrl = !!row.IEEMIFRAME_URL;
  const hasImage = !!row.IEEMIFRAME_IMAGE;
  if (hasUrl && hasImage) return 'Published';
  if (hasUrl) return 'Ready';
  return 'Draft';
};

const getIframeSummary = (rows) => {
  const total = rows.length;
  const published = rows.filter((row) => getIframeStatus(row) === 'Published').length;
  const ready = rows.filter((row) => getIframeStatus(row) === 'Ready').length;
  const draft = total - published - ready;
  return { total, published, ready, draft };
};

const getIframeBlockers = (rows) => {
  const blockers = [];
  const summary = getIframeSummary(rows);
  if (summary.total === 0) blockers.push('Belum ada iframe untuk member ini.');
  if (summary.published === 0 && summary.total > 0) blockers.push('Belum ada iframe dengan URL dan image lengkap.');
  return blockers;
};

const IeemIframeTab = ({ memberCode }) => {
  const [ieemIframeData, setIeemIframeData] = useState([]);
  const [loadingIeemIframe, setLoadingIeemIframe] = useState(false);
  const [editingIeemIframe, setEditingIeemIframe] = useState(null);
  const [deleteIeemIframe, setDeleteIeemIframe] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();
  const isMobileView = useBreakpointValue({ base: true, md: false });
  
  const { isOpen: isIeemIframeFormOpen, onOpen: onIeemIframeFormOpen, onClose: onIeemIframeFormClose } = useDisclosure();
  const { isOpen: isIeemIframeDeleteOpen, onOpen: onIeemIframeDeleteOpen, onClose: onIeemIframeDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  // IEEM Iframe form data
  const [ieemIframeFormData, setIeemIframeFormData] = useState({
    IEEMIFRAME_NAME: '',
    IEEMIFRAME_DESC: '',
    IEEMIFRAME_MEMBER: memberCode || '',
    IEEMIFRAME_URL: '',
    IEEMIFRAME_TEMPLATE: '',
    IEEMIFRAME_TERMCONDITION: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  // Fetch IEEM Iframe data
  const fetchIeemIframe = useCallback(async () => {
    setLoadingIeemIframe(true);
    try {
      const response = await ieemIframeApi.get(`/list?member=${memberCode}`);
      if (response.data.status === 200) {
        setIeemIframeData(response.data.data || []);
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Gagal mengambil data IEEM Iframe',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching IEEM Iframe:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingIeemIframe(false);
    }
  }, [toast, memberCode]);

  useEffect(() => {
    fetchIeemIframe();
  }, [fetchIeemIframe]);

  // IEEM Iframe handlers
  const handleIeemIframeInputChange = (e) => {
    const { name, value } = e.target;
    setIeemIframeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'File tidak valid',
          description: 'File harus berupa gambar (JPG, PNG, GIF)',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = '';
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File terlalu besar',
          description: 'Ukuran file maksimal 5MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleIeemIframeCreate = () => {
    setEditingIeemIframe(null);
    setIeemIframeFormData({
      IEEMIFRAME_NAME: '',
      IEEMIFRAME_DESC: '',
      IEEMIFRAME_MEMBER: memberCode || '',
      IEEMIFRAME_URL: '',
      IEEMIFRAME_TEMPLATE: '',
      IEEMIFRAME_TERMCONDITION: '',
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onIeemIframeFormOpen();
  };

  const handleIeemIframeEdit = (item) => {
    setEditingIeemIframe(item);
    setIeemIframeFormData({
      IEEMIFRAME_NAME: item.IEEMIFRAME_NAME || '',
      IEEMIFRAME_DESC: item.IEEMIFRAME_DESC || '',
      IEEMIFRAME_MEMBER: item.IEEMIFRAME_MEMBER || memberCode || '',
      IEEMIFRAME_URL: item.IEEMIFRAME_URL || '',
      IEEMIFRAME_TEMPLATE: item.IEEMIFRAME_TEMPLATE || '',
      IEEMIFRAME_TERMCONDITION: item.IEEMIFRAME_TERMCONDITION || '',
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onIeemIframeFormOpen();
  };

  const handleIeemIframeSubmit = async () => {
    if (!ieemIframeFormData.IEEMIFRAME_NAME || !ieemIframeFormData.IEEMIFRAME_MEMBER) {
      toast({
        title: 'Validasi gagal',
        description: 'Nama dan Member harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!ieemIframeFormData.IEEMIFRAME_URL) {
      toast({
        title: 'Validasi gagal',
        description: 'URL harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('IEEMIFRAME_NAME', ieemIframeFormData.IEEMIFRAME_NAME);
      formData.append('IEEMIFRAME_DESC', ieemIframeFormData.IEEMIFRAME_DESC || '');
      formData.append('IEEMIFRAME_MEMBER', ieemIframeFormData.IEEMIFRAME_MEMBER);
      formData.append('IEEMIFRAME_URL', ieemIframeFormData.IEEMIFRAME_URL);
      formData.append('IEEMIFRAME_TEMPLATE', ieemIframeFormData.IEEMIFRAME_TEMPLATE || '');
      formData.append('IEEMIFRAME_TERMCONDITION', ieemIframeFormData.IEEMIFRAME_TERMCONDITION || '');
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      if (editingIeemIframe) {
        // Update
        const response = await ieemIframeApi.post(`/update/${editingIeemIframe.IEEMIFRAME_ID}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'IEEM Iframe berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onIeemIframeFormClose();
          fetchIeemIframe();
        }
      } else {
        // Create
        const response = await ieemIframeApi.post('/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response.data.status === 201 || response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'IEEM Iframe berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onIeemIframeFormClose();
          fetchIeemIframe();
        }
      }
    } catch (error) {
      console.error('Error saving IEEM Iframe:', error);
      
      // Check for specific error types
      let errorMessage = 'Terjadi kesalahan saat menyimpan data';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Check for permission errors
      if (errorMessage.includes('permission') || errorMessage.includes('EACCES') || errorMessage.includes('not writable')) {
        errorMessage = 'Error: Folder upload tidak memiliki permission yang cukup. Silakan hubungi administrator.';
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleIeemIframeDeleteClick = (item) => {
    setDeleteIeemIframe(item);
    onIeemIframeDeleteOpen();
  };

  const handleIeemIframeDeleteConfirm = async () => {
    if (!deleteIeemIframe) return;

    try {
      const response = await ieemIframeApi.post(`/delete/${deleteIeemIframe.IEEMIFRAME_ID}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'IEEM Iframe berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onIeemIframeDeleteClose();
        setDeleteIeemIframe(null);
        fetchIeemIframe();
      }
    } catch (error) {
      console.error('Error deleting IEEM Iframe:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getImageUrl = (iframe) => {
    if (iframe.IEEMIFRAME_IMAGE) {
      return `/iframes/${iframe.IEEMIFRAME_IMAGE}`;
    }
    return null;
  };

  // Custom styles for DataTable
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f7fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
      },
    },
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
  };

  // Pagination component options
  const paginationComponentOptions = {
    rowsPerPageText: 'Baris per halaman:',
    rangeSeparatorText: 'dari',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Semua',
  };

  // Filter iframe data based on search term
  const filteredIframeData = useMemo(() => {
    if (!searchTerm) return ieemIframeData;
    const searchLower = searchTerm.toLowerCase();
    return ieemIframeData.filter((iframe) => {
      return (
        (iframe.IEEMIFRAME_NAME && iframe.IEEMIFRAME_NAME.toLowerCase().includes(searchLower)) ||
        (iframe.IEEMIFRAME_DESC && iframe.IEEMIFRAME_DESC.toLowerCase().includes(searchLower)) ||
        (iframe.IEEMIFRAME_ID && iframe.IEEMIFRAME_ID.toString().includes(searchLower)) ||
        (iframe.IEEMIFRAME_MEMBER && iframe.IEEMIFRAME_MEMBER.toLowerCase().includes(searchLower)) ||
        (iframe.IEEMIFRAME_URL && iframe.IEEMIFRAME_URL.toLowerCase().includes(searchLower)) ||
        (iframe.IEEMIFRAME_TERMCONDITION && iframe.IEEMIFRAME_TERMCONDITION.toLowerCase().includes(searchLower))
      );
    });
  }, [ieemIframeData, searchTerm]);

  const iframeSummary = useMemo(() => getIframeSummary(filteredIframeData), [filteredIframeData]);
  const iframeBlockers = useMemo(() => getIframeBlockers(filteredIframeData), [filteredIframeData]);

  return (
    <>
      <VStack align="stretch" spacing={4} mb={6}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Card borderRadius="14px" border="1px solid" borderColor="gray.100">
            <CardBody py={3}>
              <Text fontSize="xs" color="gray.500">Total Iframe</Text>
              <Text fontSize="xl" fontWeight="700" color="gray.800" fontVariantNumeric="tabular-nums">
                {iframeSummary.total}
              </Text>
            </CardBody>
          </Card>
          <Card borderRadius="14px" border="1px solid" borderColor="green.100" bg="green.50">
            <CardBody py={3}>
              <Text fontSize="xs" color="green.700">Published</Text>
              <Text fontSize="xl" fontWeight="700" color="green.800" fontVariantNumeric="tabular-nums">
                {iframeSummary.published}
              </Text>
            </CardBody>
          </Card>
          <Card borderRadius="14px" border="1px solid" borderColor="blue.100" bg="blue.50">
            <CardBody py={3}>
              <Text fontSize="xs" color="blue.700">Ready</Text>
              <Text fontSize="xl" fontWeight="700" color="blue.800" fontVariantNumeric="tabular-nums">
                {iframeSummary.ready}
              </Text>
            </CardBody>
          </Card>
          <Card borderRadius="14px" border="1px solid" borderColor="orange.100" bg="orange.50">
            <CardBody py={3}>
              <Text fontSize="xs" color="orange.700">Draft</Text>
              <Text fontSize="xl" fontWeight="700" color="orange.800" fontVariantNumeric="tabular-nums">
                {iframeSummary.draft}
              </Text>
            </CardBody>
          </Card>
        </SimpleGrid>

        <ReadinessPanel blockers={iframeBlockers} />

        <HStack spacing={3} flexWrap="wrap">
          <Button
            leftIcon={<FiPlus />}
            onClick={handleIeemIframeCreate}
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            Tambah Data
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchIeemIframe}
            isLoading={loadingIeemIframe}
            variant="outline"
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            Refresh
          </Button>
        </HStack>
      </VStack>

      <DataTableShell isLoading={loadingIeemIframe}>
      <DataTable
        columns={[
          {
            name: 'ID',
            selector: (row) => row.IEEMIFRAME_ID,
            sortable: true,
            width: '80px',
            omit: isMobileView,
          },
          {
            name: 'Gambar',
            cell: (row) => {
              const imageUrl = getImageUrl(row);
              if (imageUrl) {
                return (
                  <Image
                    src={imageUrl}
                    alt={row.IEEMIFRAME_NAME || 'Iframe Image'}
                    boxSize="50px"
                    objectFit="cover"
                    borderRadius="md"
                  />
                );
              }
              return <Text color="gray.400">-</Text>;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '100px',
            omit: isMobileView,
          },
          {
            name: 'Nama',
            selector: (row) => row.IEEMIFRAME_NAME || '-',
            sortable: true,
            cell: (row) => (
              <Text fontWeight="medium">
                {row.IEEMIFRAME_NAME || '-'}
              </Text>
            ),
          },
          {
            name: 'Deskripsi',
            selector: (row) => row.IEEMIFRAME_DESC || '-',
            sortable: true,
            cell: (row) => (
              <Text noOfLines={2} maxW="300px">
                {row.IEEMIFRAME_DESC || '-'}
              </Text>
            ),
            omit: isMobileView,
          },
          {
            name: 'Member',
            selector: (row) => row.COMPANY_NAME || row.IEEMIFRAME_MEMBER || '-',
            sortable: true,
            cell: (row) => (
              <Text>
                {row.COMPANY_NAME || row.IEEMIFRAME_MEMBER || '-'}
              </Text>
            ),
            omit: isMobileView,
          },
          {
            name: 'URL',
            cell: (row) => {
              if (row.IEEMIFRAME_URL) {
                return (
                  <HStack spacing={2}>
                    <Link
                      href={row.IEEMIFRAME_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="blue.500"
                      _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                    >
                      <HStack spacing={1}>
                        <FiExternalLink />
                        <Text fontSize="sm">Buka</Text>
                      </HStack>
                    </Link>
                  </HStack>
                );
              }
              return <Text color="gray.400">-</Text>;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            omit: isMobileView,
          },
          {
            name: 'Template',
            selector: (row) => row.IEEMIFRAME_TEMPLATE || '-',
            sortable: true,
            cell: (row) => (
              <Badge colorScheme={row.IEEMIFRAME_TEMPLATE === 'GOERS' ? 'blue' : 'green'}>
                {row.IEEMIFRAME_TEMPLATE || '-'}
              </Badge>
            ),
            omit: isMobileView,
          },
          {
            name: 'Term & Condition',
            selector: (row) => row.IEEMIFRAME_TERMCONDITION || '-',
            sortable: true,
            cell: (row) => (
              <Text noOfLines={2} maxW="300px">
                {row.IEEMIFRAME_TERMCONDITION || '-'}
              </Text>
            ),
            omit: isMobileView,
          },
          {
            name: 'Status',
            selector: (row) => getIframeStatus(row),
            sortable: true,
            cell: (row) => <StatusChip status={getIframeStatus(row)} />,
          },
          {
            name: 'Aksi',
            cell: (row) => (
              <HStack spacing={1} justify="center">
                <IconButton
                  icon={<FiEdit />}
                  size={{ base: 'xs', lg: 'sm' }}
                  colorScheme="blue"
                  variant="ghost"
                  onClick={() => handleIeemIframeEdit(row)}
                  aria-label="Edit"
                />
                <IconButton
                  icon={<FiTrash2 />}
                  size={{ base: 'xs', lg: 'sm' }}
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => handleIeemIframeDeleteClick(row)}
                  aria-label="Delete"
                />
              </HStack>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '120px',
          },
        ]}
        data={filteredIframeData}
        progressPending={loadingIeemIframe}
        progressComponent={<Text py={8}>Loading...</Text>}
        noDataComponent={
          <Text py={8} color="gray.500" textAlign="center">
            Tidak ada data IEEM Iframe
          </Text>
        }
        subHeader
        subHeaderComponent={
          <SubheaderToolbar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Cari iframe (nama, deskripsi, ID, member, URL)..."
            count={filteredIframeData.length}
          />
        }
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
        paginationComponentOptions={paginationComponentOptions}
        highlightOnHover
        pointerOnHover
        customStyles={customStyles}
        responsive
        dense
        striped
      />
      </DataTableShell>

      {/* IEEM Iframe Form Modal */}
      <Modal isOpen={isIeemIframeFormOpen} onClose={onIeemIframeFormClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingIeemIframe ? 'Edit IEEM Iframe' : 'Tambah IEEM Iframe Baru'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nama</FormLabel>
                <Input
                  name="IEEMIFRAME_NAME"
                  value={ieemIframeFormData.IEEMIFRAME_NAME}
                  onChange={handleIeemIframeInputChange}
                  placeholder="Masukkan nama iframe"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Deskripsi</FormLabel>
                <Textarea
                  name="IEEMIFRAME_DESC"
                  value={ieemIframeFormData.IEEMIFRAME_DESC}
                  onChange={handleIeemIframeInputChange}
                  placeholder="Masukkan deskripsi iframe"
                  rows={4}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Member</FormLabel>
                <Input
                  name="IEEMIFRAME_MEMBER"
                  value={ieemIframeFormData.IEEMIFRAME_MEMBER}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Gambar</FormLabel>
                <Input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.gif"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Format: JPG, PNG, GIF (Maks. 5MB)
                  {editingIeemIframe && editingIeemIframe.IEEMIFRAME_IMAGE && (
                    <Text as="span" display="block" mt={1}>
                      Gambar saat ini: {editingIeemIframe.IEEMIFRAME_IMAGE}
                    </Text>
                  )}
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>URL</FormLabel>
                <Textarea
                  name="IEEMIFRAME_URL"
                  value={ieemIframeFormData.IEEMIFRAME_URL}
                  onChange={handleIeemIframeInputChange}
                  placeholder="Masukkan URL iframe"
                  rows={3}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Contoh: https://example.com/iframe
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Template</FormLabel>
                <Select
                  name="IEEMIFRAME_TEMPLATE"
                  value={ieemIframeFormData.IEEMIFRAME_TEMPLATE}
                  onChange={handleIeemIframeInputChange}
                  placeholder="Pilih template"
                >
                  <option value="GOERS">GOERS</option>
                  <option value="Iframe Web">Iframe Web</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Pilih template untuk iframe
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Term & Condition</FormLabel>
                <Textarea
                  name="IEEMIFRAME_TERMCONDITION"
                  value={ieemIframeFormData.IEEMIFRAME_TERMCONDITION}
                  onChange={handleIeemIframeInputChange}
                  placeholder="Masukkan term & condition"
                  rows={4}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Masukkan term & condition untuk iframe
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onIeemIframeFormClose}>
              Batal
            </Button>
            <Button 
              colorScheme="brand" 
              onClick={handleIeemIframeSubmit}
              isLoading={uploadingFile}
            >
              {editingIeemIframe ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* IEEM Iframe Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isIeemIframeDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onIeemIframeDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus IEEM Iframe
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus iframe <strong>{deleteIeemIframe?.IEEMIFRAME_NAME}</strong>?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onIeemIframeDeleteClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleIeemIframeDeleteConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default IeemIframeTab;

